<template>
    <div class="ticket-theme bg-white flex-1 relative">
        <loading :status="loading.form" />
        <div class="flex relative">
            <div
                :style="{ height: 'calc(100vh - 70px)' }"
                class="flex-auto min-w-0 flex flex-col"
            >
                <div class="ticket-toolbar w-full border-b bg-white sm:px-3 flex-shrink-0">
                    <div class="ticket-toolbar-inner flex items-center justify-between min-w-0">
                        <div class="ticket-toolbar-actions flex items-center min-w-0">
                            <button
                                class="btn p-4 rounded-none"
                                type="button"
                                @click="$router.push('/dashboard/tickets')"
                            >
                                <svg-vue
                                    class="h-5 w-5 text-gray-700"
                                    icon="font-awesome.chevron-left-regular"
                                ></svg-vue>
                            </button>
                            <button
                                v-if="ticket.can_delete"
                                class="btn p-4 rounded-none"
                                type="button"
                                @click="deleteTicketModal = true"
                            >
                                <svg-vue
                                    class="h-5 w-5 text-gray-700"
                                    icon="font-awesome.trash-alt-regular"
                                ></svg-vue>
                            </button>
                        </div>
                        <div class="ticket-toolbar-meta flex items-center justify-end m-2 sm:m-0 min-w-0">
                            <div class="ticket-id-label text-xl sm:text-2xl font-semibold">
                                #{{ ticket.id }}
                            </div>
                            <div v-if="ticket.status" class="px-3">
                                <div
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800"
                                >
                                    {{ ticket.status.name }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    :class="['ticket-conversation flex flex-col bg-gray-50 min-h-0 flex-1 relative', 'ticket-chat-size-' + chatSize]"
                >
                    <div class="ticket-detail-header bg-white border-b flex-shrink-0">
                        <div class="block">
                        <div class="ticket-detail-header-inner flex items-center justify-between gap-3 max-w-full">
                            <div
                                class="ticket-detail-main min-w-0 flex-1 px-3 sm:px-5 py-2"
                            >
                                <div class="text-base sm:text-lg font-semibold truncate">
                                    {{ ticket.subject }}
                                </div>
                                <template
                                    v-for="(label, index) in ticket.labels"
                                >
                                    <div
                                        :style="{
                                            backgroundColor: label.color
                                        }"
                                        class="inline-flex items-center px-2 py-0.5 mr-1 rounded text-xs font-medium leading-4 text-gray-100"
                                    >
                                        {{ label.name }}
                                        <button
                                            class="flex-shrink-0 ml-1.5 inline-flex text-gray-100 focus:outline-none focus:text-gray-100 cursor-pointer"
                                            type="button"
                                            @click="removeLabel(index)"
                                        >
                                            <svg-vue
                                                class="h-3 w-3"
                                                icon="font-awesome.times-solid"
                                            ></svg-vue>
                                        </button>
                                    </div>
                                </template>
                            </div>
                            <div
                                class="ticket-detail-controls flex-shrink-0 px-3 sm:px-5 py-2"
                            >
                                <div class="flex items-end gap-2">
                                    <div class="ticket-created-at text-xs text-gray-500 hidden sm:block">
                                        {{ ticket.created_at | momentFormatDateTimeAgo }}
                                    </div>
                                    <div class="ticket-size-switch inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 shadow-sm" :title="$t('Display size')">
                                        <button
                                            type="button"
                                            :class="chatSizeButtonClass('compact')"
                                            @click="setChatSize('compact')"
                                        >
                                            {{ $t('Small') }}
                                        </button>
                                        <button
                                            type="button"
                                            :class="chatSizeButtonClass('normal')"
                                            @click="setChatSize('normal')"
                                        >
                                            {{ $t('Normal') }}
                                        </button>
                                        <button
                                            type="button"
                                            :class="chatSizeButtonClass('comfortable')"
                                            @click="setChatSize('comfortable')"
                                        >
                                            {{ $t('Large') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>
                    <div class="ticket-aside-toggle lg:hidden sticky top-0 z-10 px-3 py-2 bg-white border-b border-gray-200 shadow-sm">
                        <button
                            :class="mobileAsideButtonClass"
                            class="ticket-aside-toggle-btn w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold shadow-sm transition"
                            type="button"
                            @click="mobileAsideOpen = true"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.users-class-regular"></svg-vue>
                            <span>{{ $t('Approval & Agents') }}</span>
                            <span
                                v-if="mobileAsideBadgeText"
                                class="ticket-aside-toggle-badge inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-xs font-bold"
                            >
                                {{ mobileAsideBadgeText }}
                            </span>
                        </button>
                    </div>
                    <div
                        ref="conversationBody"
                        class="ticket-messages flex-1 min-h-0 overflow-y-auto bg-gray-50"
                        @scroll="handleConversationScroll"
                    >
                        <template v-if="messageTimelineItems.length > 0">
                            <div class="block">
                                <div
                                    v-for="timelineItem in messageTimelineItems"
                                    :key="timelineItem.key"
                                >
                                    <div
                                        v-if="timelineItem.type === 'divider'"
                                        class="px-6 py-3"
                                    >
                                        <div class="flex items-center">
                                            <div class="flex-1 border-t border-blue-200"></div>
                                            <button
                                                class="mx-3 inline-flex items-center px-3 py-1 rounded-full bg-white text-blue-700 text-xs font-semibold shadow-sm border border-blue-200 hover:bg-blue-50 focus:outline-none"
                                                type="button"
                                                @click="acknowledgeNewReplies"
                                            >
                                                {{ $t("New message") }}
                                            </button>
                                            <div class="flex-1 border-t border-blue-200"></div>
                                        </div>
                                    </div>
                                    <div
                                        v-else-if="timelineItem.type === 'approval'"
                                        class="flex justify-center px-6 py-1.5"
                                    >
                                        <div
                                            :class="timelineItem.approval.status === 'approved'
                                                ? 'bg-green-100 text-green-800 border-green-200'
                                                : 'bg-red-100 text-red-800 border-red-200'"
                                            class="inline-flex flex-wrap items-center justify-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold shadow-sm"
                                        >
                                            <svg-vue
                                                class="h-3.5 w-3.5"
                                                :icon="timelineItem.approval.status === 'approved'
                                                    ? 'font-awesome.check-circle-solid'
                                                    : 'font-awesome.times-circle-solid'"
                                            ></svg-vue>
                                            <span>
                                                {{ timelineItem.approval.user ? timelineItem.approval.user.name : '-' }}
                                                {{ timelineItem.approval.status === 'approved' ? $t("approved this ticket") : $t("rejected this ticket") }}
                                            </span>
                                            <span class="font-normal opacity-60">&bull;</span>
                                            <span class="font-normal">
                                                {{ timelineItem.approval.acted_at | momentFormatDateTime }}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        v-else
                                        :data-message-reply-id="timelineItem.reply.id"
                                        :class="[
                                            timelineItem.index > 0 ? 'border-t' : '',
                                            isOwnReply(timelineItem.reply)
                                                ? 'justify-end'
                                                : 'justify-start',
                                            { 'ticket-reply-highlight': Number(highlightedReplyId) === Number(timelineItem.reply.id) }
                                        ]"
                                        class="ticket-reply-item flex"
                                    >
                                        <div
                                            v-if="!isOwnReply(timelineItem.reply)"
                                            class="ticket-message-avatar ticket-message-avatar-left flex-shrink-0"
                                        >
                                            <img
                                                v-if="getReplyAvatar(timelineItem.reply)"
                                                :src="getReplyAvatar(timelineItem.reply)"
                                                :alt="getReplyAuthorName(timelineItem.reply)"
                                                class="h-full w-full rounded-full object-cover cursor-zoom-in"
                                                @click="openImageViewer(getReplyAvatar(timelineItem.reply), getReplyAuthorName(timelineItem.reply))"
                                            />
                                            <span v-else>{{ getReplyInitials(timelineItem.reply) }}</span>
                                        </div>
                                        <div
                                            :class="
                                                isOwnReply(timelineItem.reply)
                                                    ? 'ticket-message-bubble-own bg-blue-100 border-blue-300'
                                                    : 'ticket-message-bubble-other bg-white border-gray-300'
                                            "
                                            class="ticket-message-bubble w-full border rounded-2xl shadow-sm"
                                        >
                                        <div
                                            class="md:flex md:items-center pb-1"
                                        >
                                            <div
                                                class="ticket-message-author md:flex-1 font-semibold text-gray-800 flex items-center flex-wrap"
                                            >
                                                <span>{{
                                                    timelineItem.reply.user.name
                                                }}</span>
                                                <span
                                                    v-for="role in timelineItem.reply.sender_roles || []"
                                                    :key="role"
                                                    :class="roleBadgeClass(role)"
                                                    class="ml-2 px-2 py-0.5 rounded text-xs font-semibold"
                                                >
                                                    {{ roleBadgeLabel(role) }}
                                                </span>
                                            </div>
                                            <div class="md:flex-1">
                                                <div
                                                    class="ticket-message-meta md:float-right text-gray-500 flex items-center"
                                                >
                                                    {{
                                                        timelineItem.reply.created_at
                                                            | momentFormatDateTime
                                                    }}
                                                    <span
                                                        v-if="
                                                            timelineItem.reply.is_edited
                                                        "
                                                        class="ml-2 text-xs text-gray-500"
                                                        >{{
                                                            $t("Edited")
                                                        }}</span
                                                    >
                                                    <button
                                                        v-if="
                                                            timelineItem.reply.can_edit
                                                        "
                                                        class="ml-2 text-xs text-blue-600 hover:text-blue-800 focus:outline-none"
                                                        type="button"
                                                        @click="
                                                            startEditReply(
                                                                timelineItem.reply
                                                            )
                                                        "
                                                    >
                                                        {{ $t("Edit") }}
                                                    </button>
                                                    <button
                                                        class="ml-2 inline-flex items-center rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 focus:outline-none"
                                                        type="button"
                                                        @click="startReplyToMessage(timelineItem.reply)"
                                                    >
                                                        {{ $t("Reply") }}
                                                    </button>
                                                    <button
                                                        v-if="timelineItem.reply"
                                                        :title="
                                                            $t(
                                                                'See who has read this message'
                                                            )
                                                        "
                                                        class="ml-2 focus:outline-none"
                                                        type="button"
                                                        @click="
                                                            toggleSeenBy(
                                                                timelineItem.reply.id
                                                            )
                                                        "
                                                    >
                                                        <span
                                                            v-if="
                                                                timelineItem.reply.read_by_count >
                                                                    0
                                                            "
                                                            class="inline-flex items-center text-blue-500"
                                                        >
                                                            <svg-vue
                                                                class="h-3 w-3 -mr-1"
                                                                icon="font-awesome.check-circle-solid"
                                                            ></svg-vue>
                                                            <svg-vue
                                                                class="h-3 w-3"
                                                                icon="font-awesome.check-circle-solid"
                                                            ></svg-vue>
                                                        </span>
                                                        <span
                                                            v-else
                                                            class="inline-flex items-center text-gray-400"
                                                        >
                                                            <svg-vue
                                                                class="h-3 w-3"
                                                                icon="font-awesome.check-circle-light"
                                                            ></svg-vue>
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            v-if="
                                                editingReplyId ===
                                                    timelineItem.reply.id
                                            "
                                            class="mt-2"
                                        >
                                            <textarea
                                                v-model="editingReplyBody"
                                                class="w-full form-textarea"
                                                rows="5"
                                            ></textarea>
                                            <div class="mt-2 flex justify-end">
                                                <button
                                                    class="btn btn-white mr-2"
                                                    type="button"
                                                    @click="cancelEditReply"
                                                >
                                                    {{ $t("Cancel") }}
                                                </button>
                                                <button
                                                    :disabled="
                                                        loading.editReply
                                                    "
                                                    class="btn btn-blue"
                                                    type="button"
                                                    @click="
                                                        updateReply(timelineItem.reply)
                                                    "
                                                >
                                                    {{ $t("Save") }}
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            v-else
                                            class="ticket-message-body text-gray-700 ticket-reply-body"
                                            v-html="timelineItem.reply.body"
                                            @click="handleMessageBodyClick"
                                        ></div>
                                        <div
                                            v-if="timelineItem.reply.status_change"
                                            class="mt-2 inline-flex items-center text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 px-3 py-1"
                                        >
                                            {{ $t("Status changed") }}:
                                            {{
                                                timelineItem.reply.status_change.from
                                                    .name
                                            }}
                                            ->
                                            {{
                                                timelineItem.reply.status_change.to
                                                    .name
                                            }}
                                        </div>
                                        <div
                                            v-if="seenByOpen[timelineItem.reply.id]"
                                            class="mt-2 rounded-lg border border-gray-200 bg-white p-2"
                                        >
                                            <div
                                                class="text-xs font-semibold text-gray-600 mb-1"
                                            >
                                                {{ $t("Read by") }}
                                            </div>
                                            <template
                                                v-if="
                                                    timelineItem.reply.read_by_users &&
                                                        timelineItem.reply
                                                            .read_by_users
                                                            .length > 0
                                                "
                                            >
                                                <div
                                                    v-for="reader in timelineItem.reply.read_by_users"
                                                    :key="reader.id"
                                                    class="flex items-center justify-between text-xs text-gray-600 py-1"
                                                >
                                                    <div
                                                        class="flex items-center"
                                                    >
                                                        <img
                                                            :alt="$t('Avatar')"
                                                            :src="
                                                                reader.avatar !==
                                                                'gravatar'
                                                                    ? reader.avatar
                                                                    : reader.gravatar
                                                            "
                                                            class="h-5 w-5 rounded-full mr-2 object-cover cursor-zoom-in"
                                                            @click="openImageViewer(reader.avatar !== 'gravatar' ? reader.avatar : reader.gravatar, reader.name)"
                                                        />
                                                        <span>{{
                                                            reader.name
                                                        }}</span>
                                                    </div>
                                                    <span>{{
                                                        formatReadAt(
                                                            reader.read_at
                                                        )
                                                    }}</span>
                                                </div>
                                            </template>
                                            <div
                                                v-else
                                                class="text-xs text-gray-500 py-1"
                                            >
                                                {{
                                                    $t(
                                                        "No one has read this message yet"
                                                    )
                                                }}
                                            </div>
                                        </div>
                                        <template
                                            v-if="visibleReplyAttachments(timelineItem.reply).length > 0"
                                        >
                                            <div class="ticket-chat-attachments mt-4">
                                                <div
                                                    v-for="attachment in visibleReplyAttachments(timelineItem.reply)"
                                                    :key="attachment.id || attachment.uuid || attachment.name"
                                                    class="ticket-chat-attachment-item"
                                                >
                                                    <button
                                                        v-if="isImageAttachment(attachment) && getAttachmentPreviewUrl(attachment)"
                                                        class="ticket-chat-image-link"
                                                        type="button"
                                                        @click="openImageViewer(getAttachmentPreviewUrl(attachment), getAttachmentName(attachment))"
                                                    >
                                                        <img
                                                            :src="getAttachmentPreviewUrl(attachment)"
                                                            :alt="getAttachmentName(attachment)"
                                                            class="ticket-chat-image"
                                                            loading="lazy"
                                                        />
                                                        <span class="ticket-chat-image-name">{{ getAttachmentName(attachment) }}</span>
                                                    </button>
                                                    <attachment
                                                        v-else
                                                        :details="attachment"
                                                        :remove-button="false"
                                                    />
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                    <div
                                        v-if="isOwnReply(timelineItem.reply)"
                                        class="ticket-message-avatar ticket-message-avatar-right flex-shrink-0"
                                    >
                                        <img
                                            v-if="getReplyAvatar(timelineItem.reply)"
                                            :src="getReplyAvatar(timelineItem.reply)"
                                            :alt="getReplyAuthorName(timelineItem.reply)"
                                            class="h-full w-full rounded-full object-cover cursor-zoom-in"
                                            @click="openImageViewer(getReplyAvatar(timelineItem.reply), getReplyAuthorName(timelineItem.reply))"
                                        />
                                        <span v-else>{{ getReplyInitials(timelineItem.reply) }}</span>
                                    </div>
                                </div>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="h-full flex border-t">
                                <div class="m-auto">
                                    <div
                                        class="grid grid-cols-1 justify-items-center h-full w-full py-24"
                                    >
                                        <div
                                            class="flex justify-center items-center"
                                        >
                                            <svg-vue
                                                class="h-full h-auto w-48 mb-6"
                                                icon="undraw.task-list"
                                            ></svg-vue>
                                        </div>
                                        <div
                                            class="flex justify-center items-center"
                                        >
                                            <div
                                                class="w-full font-semibold text-2xl"
                                            >
                                                {{
                                                    $t(
                                                        "This ticket has no conversations"
                                                    )
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                    <button
                        v-show="showJumpToBottomButton"
                        class="ticket-jump-bottom"
                        type="button"
                        @click="jumpToConversationBottom"
                        :title="$t('Go to latest message')"
                    >
                        <span
                            v-if="hasNewIncomingReply"
                            class="ticket-jump-bottom-badge"
                        >
                            {{ $t('New') }}
                        </span>
                        <svg
                            class="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>
                    <div class="ticket-composer bg-white border-t flex-shrink-0">
                        <div v-if="isCcParticipant" class="px-4 py-3 text-sm text-gray-500 bg-gray-50 rounded-xl">
                            {{ $t('You are listed as CC on this ticket — you can read the conversation but cannot reply') }}.
                        </div>
                        <div v-show="!replyForm && !isCcParticipant" class="flex items-center gap-3">
                            <button
                                class="ticket-action-button flex-1 inline-flex items-center justify-center rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-sm focus:outline-none"
                                type="button"
                                @click="openReplyComposer()"
                            >
                                {{ $t("Send message") }}
                            </button>
                        </div>

                        <div
                            v-if="composerReady"
                            v-show="replyForm"
                            ref="composerPanel"
                            class="ticket-composer-panel relative rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden"
                            @paste.capture="handleComposerPaste"
                            @dragover.prevent
                            @drop.prevent="handleComposerDrop"
                        >
                            <div class="ticket-composer-header sticky top-0 z-10 flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-gray-100">
                                <div class="flex items-center gap-2.5 min-w-0">
                                    <span class="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white shadow-sm" style="background: var(--ticket-accent);">
                                        <svg-vue class="h-3.5 w-3.5" icon="font-awesome.reply-regular"></svg-vue>
                                    </span>
                                    <button
                                        class="min-w-0 text-left focus:outline-none"
                                        :class="replyingToReply ? 'cursor-pointer' : 'cursor-default'"
                                        type="button"
                                        @click="replyingToReply && focusReplyById(replyingToReply.id)"
                                    >
                                        <div class="text-sm font-semibold text-gray-900 leading-tight truncate">
                                            <template v-if="replyingToReply">
                                                {{ $t("Replying to") }} {{ getReplyAuthorName(replyingToReply) }}
                                            </template>
                                            <template v-else>{{ $t("New message") }}</template>
                                        </div>
                                        <div
                                            v-if="replyingToReply"
                                            class="text-xs text-gray-500 truncate"
                                            v-html="getReplyPreview(replyingToReply)"
                                        ></div>
                                    </button>
                                </div>
                                <div class="flex items-center gap-1.5 flex-shrink-0">
                                    <button
                                        v-if="replyingToReply"
                                        class="inline-flex h-7 items-center px-2.5 rounded-full text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none transition-colors"
                                        type="button"
                                        @click="cancelReplyToMessage"
                                        :title="$t('Cancel reply')"
                                    >
                                        {{ $t('Cancel reply') }}
                                    </button>
                                    <button
                                        class="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none transition-colors"
                                        type="button"
                                        @click="discardReply"
                                        :title="$t('Close')"
                                    >
                                        <svg-vue class="h-3.5 w-3.5" icon="font-awesome.times-light"></svg-vue>
                                    </button>
                                </div>
                            </div>

                            <loading :status="loading.reply" />

                            <form @submit.prevent="addReply" class="p-3">
                                <div
                                    v-if="composerImagePreviews.length > 0"
                                    class="ticket-image-preview-strip mb-2"
                                >
                                    <div
                                        v-for="preview in composerImagePreviews"
                                        :key="preview.key"
                                        class="ticket-image-preview-card"
                                    >
                                        <img
                                            :src="preview.url"
                                            :alt="preview.name"
                                            class="ticket-image-preview-img"
                                        />
                                        <button
                                            class="ticket-image-preview-remove"
                                            type="button"
                                            @click="removeComposerImagePreview(preview)"
                                        >
                                            ×
                                        </button>
                                        <div
                                            v-if="preview.status === 'uploading'"
                                            class="ticket-image-preview-status"
                                        >
                                            {{ $t("Uploading") }}
                                        </div>
                                        <div
                                            v-if="preview.status === 'error'"
                                            class="ticket-image-preview-status ticket-image-preview-error"
                                        >
                                            {{ $t("Failed") }}
                                        </div>
                                    </div>
                                </div>

                                <template v-if="nonImageComposerAttachments.length > 0">
                                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mb-2">
                                        <attachment
                                            v-for="item in nonImageComposerAttachments"
                                            :key="item.attachment.id || item.attachment.uuid || item.attachment.name || item.index"
                                            :details="item.attachment"
                                            v-on:remove="removeAttachment(item.index)"
                                        />
                                    </div>
                                </template>

                                <div
                                    v-if="maxFileSizeLabel"
                                    class="text-xs text-gray-400 mb-2"
                                >
                                    {{ $t("Max file size: {size}", { size: maxFileSizeLabel }) }}
                                </div>

                                <input-wysiwyg
                                    ref="replyComposer"
                                    id="ticket_body"
                                    v-model="ticketReply.body"
                                    :cannedReplyList="cannedReplyList"
                                    :plugins="{
                                        images: true,
                                        cannedReply: true,
                                        attachment: true,
                                        shortCode: true
                                    }"
                                    @selectUploadFile="selectUploadFile"
                                    @focusin.native="scheduleBindEditorPasteHandlers"
                                    @paste.native.capture="handleComposerPaste"
                                    @drop.native.prevent="handleComposerDrop"
                                >
                                    <template v-slot:top>
                                        <div
                                            :class="{
                                                'bg-gray-200': uploadingFileProgress > 0
                                            }"
                                            class="h-1 w-full"
                                        >
                                            <div
                                                :style="{
                                                    width: uploadingFileProgress + '%'
                                                }"
                                                class="bg-blue-500 py-0.5"
                                            ></div>
                                        </div>
                                    </template>
                                    <template v-slot:bottom>
                                        <div class="ticket-composer-actions flex items-center justify-between border border-t-0 bg-white">
                                            <div class="flex items-center">
                                                <button
                                                    class="btn btn-secondary rounded-none"
                                                    type="button"
                                                    @click="discardReply"
                                                >
                                                    {{ $t("Discard") }}
                                                </button>
                                            </div>
                                            <div class="flex">
                                                <select
                                                    id="status"
                                                    v-model="ticketReply.status_id"
                                                    :disabled="!canChangeStatus"
                                                    aria-label="Sort by"
                                                    class="block form-select pl-3 pr-9 py-2 border-l border-r-0 border-t-0 border-b-0 border-gray-400 rounded-none bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                                >
                                                    <option
                                                        v-for="status in statusList"
                                                        :key="status.id"
                                                        :value="status.id"
                                                    >
                                                        {{ status.name }}
                                                    </option>
                                                </select>
                                                <button
                                                    :disabled="loading.reply || loading.file"
                                                    class="ticket-submit-button rounded-none px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                    type="submit"
                                                >
                                                    {{ loading.reply ? $t("Sending") : $t("Send message") }}
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            v-if="canCreatorCloseByReply"
                                            class="text-xs text-blue-700 px-3 py-2 border-t"
                                        >
                                            {{ $t("To cancel this ticket, send a reply with status Closed") }}
                                        </div>
                                        <div
                                            v-if="!canChangeStatus"
                                            class="text-xs text-yellow-700 px-3 py-2 border-t"
                                        >
                                            {{ statusChangeHint }}
                                        </div>
                                    </template>
                                </input-wysiwyg>
                            </form>

                            <input
                                ref="fileInput"
                                hidden
                                type="file"
                                multiple
                                @change="uploadFile($event)"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div
                :style="{ flex: '0 0 240px', width: '240px' }"
                class="bg-gray-100 hidden lg:block border-l"
            >
                <div class="p-5 overflow-y-auto" style="max-height: calc(100vh - 70px);">
                    <ticket-aside
                        :ticket="ticket"
                        :follow-up-loading="followUp.loading"
                        :follow-up-remaining-minutes="followUp.remainingMinutes"
                        @approve="approveTicket"
                        @reject="rejectTicket"
                        @follow-up="followUpTicket"
                        @cc-updated="getTicket(true)"
                    ></ticket-aside>
                </div>
            </div>
        </div>

        <transition
            enter-active-class="ease-out duration-300"
            enter-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="ease-in duration-200"
            leave-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="mobileAsideOpen"
                class="ticket-aside-drawer fixed inset-0 z-40 lg:hidden"
            >
                <div class="ticket-aside-drawer-backdrop absolute inset-0" @click="closeMobileAside"></div>
                <div class="ticket-aside-drawer-panel absolute bottom-0 inset-x-0 bg-white rounded-t-2xl shadow-2xl flex flex-col">
                    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <div class="font-semibold text-gray-900">{{ $t('Approval & Agents') }}</div>
                        <button
                            class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none"
                            type="button"
                            @click="closeMobileAside"
                            :title="$t('Close')"
                        >×</button>
                    </div>
                    <div class="overflow-y-auto p-4" style="max-height: 78vh;">
                        <ticket-aside
                            :ticket="ticket"
                            :follow-up-loading="followUp.loading"
                            :follow-up-remaining-minutes="followUp.remainingMinutes"
                            @approve="onAsideAction('approve')"
                            @reject="onAsideAction('reject')"
                            @follow-up="followUpTicket"
                            @cc-updated="getTicket(true)"
                        ></ticket-aside>
                    </div>
                </div>
            </div>
        </transition>

        <image-lightbox
            :open="imageViewerOpen"
            :url="imageViewerUrl || ''"
            :name="imageViewerName || ''"
            @close="closeImageViewer"
        />

        <div
            v-if="ticket.can_delete"
            v-show="deleteTicketModal"
            class="fixed z-20 inset-0 overflow-y-auto"
        >
            <div
                class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
            >
                <transition
                    duration="300"
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div
                        v-show="deleteTicketModal"
                        class="fixed inset-0 transition-opacity"
                    >
                        <div
                            class="absolute inset-0 bg-gray-500 opacity-75"
                        ></div>
                    </div>
                </transition>
                <span
                    class="hidden sm:inline-block sm:align-middle sm:h-screen"
                ></span
                >&#8203;
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="deleteTicketModal"
                        aria-labelledby="modal-headline"
                        aria-modal="true"
                        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                        role="dialog"
                    >
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <svg-vue
                                        class="h-6 w-6 pb-1 text-red-600"
                                        icon="font-awesome.exclamation-triangle-light"
                                    ></svg-vue>
                                </div>
                                <div
                                    class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left"
                                >
                                    <h3
                                        id="modal-headline"
                                        class="text-lg leading-6 font-medium text-gray-900"
                                    >
                                        {{ $t("Delete ticket") }}
                                    </h3>
                                    <div class="mt-2">
                                        <p
                                            class="text-sm leading-5 text-gray-500"
                                        >
                                            {{
                                                $t(
                                                    "Are you sure you want to delete the ticket?"
                                                )
                                            }}
                                            {{
                                                $t(
                                                    "All data will be permanently removed"
                                                )
                                            }}.
                                            {{
                                                $t(
                                                    "All related data will be deleted"
                                                )
                                            }}.
                                            {{
                                                $t(
                                                    "This action cannot be undone"
                                                )
                                            }}.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse"
                        >
                            <button
                                class="btn btn-red mr-2 sm:mr-0"
                                type="button"
                                @click="deleteTicket"
                            >
                                {{ $t("Delete ticket") }}
                            </button>
                            <button
                                class="btn btn-white mr-0 sm:mr-2"
                                type="button"
                                @click="deleteTicketModal = false"
                            >
                                {{ $t("Cancel") }}
                            </button>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
import { mixin as clickaway } from "vue-clickaway";
import TicketAside from "./TicketAside.vue";

export default {
    name: "manage",
    components: { TicketAside },
    mixins: [clickaway],
    metaInfo() {
        return {
            title: this.$i18n.t("Manage ticket")
        };
    },
    watch: {
        mobileAsideOpen(value) {
            if (typeof document !== "undefined" && document.body) {
                document.body.style.overflow = value ? "hidden" : "";
            }
        }
    },
    mounted() {
        this.loadChatSize();
        this.getTicket();
        this.getFilters();
        this.getCannedReplies();
        this.startTicketRealtime();
        this.startVisibleSync();
        this.preloadComposer();
        this.scheduleBindEditorPasteHandlers();
        document.addEventListener("paste", this.handleDocumentPaste, true);
        window.addEventListener("paste", this.handleDocumentPaste, true);
    },
    beforeDestroy() {
        this.stopVisibleSync();
        this.stopTicketRealtime();
        if (typeof document !== "undefined" && document.body) {
            document.body.style.overflow = "";
        }
        if (this.highlightReplyTimer) {
            clearTimeout(this.highlightReplyTimer);
            this.highlightReplyTimer = null;
        }
        if (this.composerPreloadTimer) {
            clearTimeout(this.composerPreloadTimer);
            this.composerPreloadTimer = null;
        }
        if (this.followUpTimer) {
            clearInterval(this.followUpTimer);
            this.followUpTimer = null;
        }
        this.revokeComposerImagePreviews();
        this.unbindEditorPasteHandlers();
        if (this.editorPasteRetryTimer) {
            clearTimeout(this.editorPasteRetryTimer);
            this.editorPasteRetryTimer = null;
        }
        document.removeEventListener("paste", this.handleDocumentPaste, true);
        window.removeEventListener("paste", this.handleDocumentPaste, true);
    },
    data() {
        return {
            loading: {
                form: true,
                reply: false,
                file: false,
                editReply: false
            },
            // Status tombol follow-up: loading saat request berjalan, dan
            // sisa menit cooldown dari server (429) untuk menonaktifkan tombol.
            followUp: {
                loading: false,
                remainingMinutes: 0
            },
            followUpTimer: null,
            deleteTicketModal: false,
            mobileAsideOpen: false,
            replyForm: false,
            uploadingFileProgress: 0,
            ticket: {
                subject: null,
                created_at: null,
                labels: [],
                ticketReplies: []
            },
            ticketReply: {
                status_id: null,
                body: "",
                attachments: []
            },
            actions: {
                agent: false,
                department: false,
                label: false,
            },
            cannedReplyList: [],
            agentList: [],
            departmentList: [],
            labelList: [],
            statusList: [],
            seenByOpen: {},
            realtimeChannel: null,
            realtimeUserChannel: null,
            lastIncomingReplyId: null,
            hasNewIncomingReply: false,
            lastRealtimeReplyId: null,
            realtimeRefreshTimer: null,
            lastRealtimeEventKey: null,
            visibleSyncTimer: null,
            editingReplyId: null,
            editingReplyBody: "",
            editingReplyQuoteHtml: "",
            imageViewerOpen: false,
            imageViewerUrl: null,
            imageViewerName: null,

            markReadInProgress: false,
            realtimeRequestInProgress: false,
            autoScrollEnabled: true,
            isNearConversationBottom: true,
            showJumpToBottomButton: false,
            lastPasteSignature: null,
            newMessageDividerReplyId: null,
            replyingToReply: null,
            replyQuotePreviewMaxLength: 160,
            chatSize: "normal",
            highlightedReplyId: null,
            highlightReplyTimer: null,
            composerReady: false,
            composerPreloadTimer: null,
            composerImagePreviews: [],
            pendingUploadCount: 0,
            editorPasteHandler: null,
            editorPasteRetryTimer: null
        };
    },
    filters: {
        momentFormatDateTime: function(value) {
            return moment(value)
                .locale(window.app.app_date_locale)
                .format(window.app.app_date_format + " HH:mm");
        },
        momentFormatDateTimeAgo: function(value) {
            return moment(value)
                .locale(window.app.app_date_locale)
                .fromNow();
        }
    },
    computed: {
        isCcParticipant() {
            const user = this.$store && this.$store.state.user ? this.$store.state.user : null;
            if (!user) {
                return false;
            }
            return (this.ticket.cc_user_ids || []).indexOf(Number(user.id)) > -1;
        },
        orderedTicketReplies() {
            return (this.ticket.ticketReplies || [])
                .slice()
                .sort((a, b) => Number(a.id) - Number(b.id));
        },
        nonImageComposerAttachments() {
            return (this.ticketReply.attachments || [])
                .map((attachment, index) => ({ attachment, index }))
                .filter(item => !this.isImageAttachment(item.attachment));
        },
        maxFileSize() {
            return Number(
                (typeof window !== "undefined" &&
                    window.app &&
                    window.app.upload_max_size) ||
                    0
            );
        },
        maxFileSizeLabel() {
            const bytes = this.maxFileSize;
            if (!bytes) {
                return "";
            }

            const mb = bytes / (1024 * 1024);
            if (mb >= 1024) {
                const gb = mb / 1024;
                return (Number.isInteger(gb) ? gb : gb.toFixed(1)) + " GB";
            }

            return (Number.isInteger(mb) ? mb : mb.toFixed(1)) + " MB";
        },
        messageTimelineItems() {
            const items = [];
            const dividerReplyId = this.newMessageDividerReplyId
                ? Number(this.newMessageDividerReplyId)
                : null;
            let dividerAdded = false;

            // Event approve/reject approver ditampilkan di chat seperti
            // perubahan status, digabung sesuai waktu aksinya.
            const pendingApprovalEvents = (this.ticket.approvers || [])
                .filter(
                    approver =>
                        approver.acted_at &&
                        (approver.status === "approved" ||
                            approver.status === "rejected")
                )
                .slice()
                .sort(
                    (a, b) =>
                        new Date(a.acted_at) - new Date(b.acted_at)
                )
                .map(approver => ({
                    type: "approval",
                    key: "approval-" + approver.id,
                    approval: approver
                }));

            let replyIndex = 0;
            this.orderedTicketReplies.forEach(reply => {
                const replyId = Number(reply.id) || 0;

                // Event approval yang terjadi sebelum reply ini ditampilkan lebih dulu.
                while (
                    pendingApprovalEvents.length > 0 &&
                    new Date(pendingApprovalEvents[0].approval.acted_at) <=
                        new Date(reply.created_at)
                ) {
                    items.push(pendingApprovalEvents.shift());
                }

                if (
                    this.hasNewIncomingReply &&
                    dividerReplyId !== null &&
                    !dividerAdded &&
                    replyId >= dividerReplyId
                ) {
                    items.push({
                        type: "divider",
                        key: "new-message-divider-" + dividerReplyId
                    });
                    dividerAdded = true;
                }

                items.push({
                    type: "reply",
                    key: "reply-" + replyId,
                    reply: reply,
                    index: replyIndex
                });
                replyIndex += 1;
            });

            // Event approval setelah reply terakhir.
            while (pendingApprovalEvents.length > 0) {
                items.push(pendingApprovalEvents.shift());
            }

            return items;
        },
        approvalPending() {
            return (
                this.ticket.approval_required &&
                this.ticket.approval_status === "pending"
            );
        },
        canCreatorCloseByReply() {
            const authUser = this.$store.state.user;
            if (!authUser || !this.ticket || !this.ticket.approval_required) {
                return false;
            }
            if (this.ticket.approval_status !== "pending") {
                return false;
            }
            return Number(this.ticket.created_by) === Number(authUser.id);
        },
        canChangeStatus() {
            return (
                this.ticket.can_change_status !== false ||
                this.canCreatorCloseByReply
            );
        },
        statusChangeHint() {
            if (
                this.ticket.approval_required &&
                this.ticket.approval_status !== "approved"
            ) {
                return this.$t(
                    "Status can be changed only after ticket approval"
                );
            }
            return this.$t(
                "Only ticket executors, customer, or creator can change the status"
            );
        },
        approverNames() {
            if (!this.ticket.approvers || this.ticket.approvers.length === 0) {
                return "-";
            }
            return this.ticket.approvers
                .map(item => (item.user ? item.user.name : "-"))
                .join(", ");
        },
        approvalFlowClass() {
            if (this.ticket.approval_status === "approved") {
                return "border-green-300 bg-green-50 text-green-900";
            }
            if (
                this.ticket.approval_status === "rejected" ||
                this.ticket.approval_status === "canceled"
            ) {
                return "border-red-300 bg-red-50 text-red-900";
            }
            return "border-yellow-300 bg-yellow-50 text-yellow-900";
        },
        approvalStatusBadgeClass() {
            if (this.ticket.approval_status === "approved") {
                return "bg-green-100 text-green-800";
            }
            if (
                this.ticket.approval_status === "rejected" ||
                this.ticket.approval_status === "canceled"
            ) {
                return "bg-red-100 text-red-800";
            }
            return "bg-yellow-100 text-yellow-800";
        },
        approvalStatusLabel() {
            if (this.ticket.approval_status === "approved") {
                return this.$t("Approved");
            }
            if (this.ticket.approval_status === "rejected") {
                return this.$t("Rejected");
            }
            if (this.ticket.approval_status === "canceled") {
                return this.$t("Canceled");
            }
            return this.$t("Pending");
        },
        deadlineUrgent() {
            if (!this.ticket.due_at) {
                return false;
            }
            const due = moment(this.ticket.due_at).startOf("day");
            const limit = moment().startOf("day").add(3, "days");
            return due.isSameOrBefore(limit);
        },
        mobileAsideNeedsAttention() {
            return this.approvalPending || this.deadlineUrgent;
        },
        mobileAsideBadgeText() {
            if (this.approvalPending) {
                return "!";
            }
            return "";
        },
        mobileAsideButtonClass() {
            if (this.mobileAsideNeedsAttention) {
                return "ticket-aside-toggle-urgent";
            }
            return "ticket-aside-toggle-default";
        }
    },
    methods: {
        preloadComposer() {
            if (this.composerReady) {
                return;
            }

            const makeReady = () => {
                this.composerReady = true;
                this.$nextTick(() => this.scheduleBindEditorPasteHandlers());
            };

            if (window.requestIdleCallback) {
                window.requestIdleCallback(makeReady, { timeout: 500 });
                return;
            }

            this.composerPreloadTimer = setTimeout(makeReady, 120);
        },
        loadChatSize() {
            const savedSize = window.localStorage
                ? window.localStorage.getItem("ticket_chat_size")
                : null;

            if (["compact", "normal", "comfortable"].indexOf(savedSize) > -1) {
                this.chatSize = savedSize;
                return;
            }

            if (window.innerWidth && window.innerWidth < 640) {
                this.chatSize = "compact";
            }
        },
        setChatSize(size) {
            if (["compact", "normal", "comfortable"].indexOf(size) === -1) {
                return;
            }

            this.chatSize = size;

            if (window.localStorage) {
                window.localStorage.setItem("ticket_chat_size", size);
            }

            this.$nextTick(() => {
                if (this.isNearConversationBottom) {
                    this.scrollToLatestReply(true);
                }
            });
        },
        chatSizeButtonClass(size) {
            return [
                "px-2.5 py-1 rounded-md text-xs font-semibold transition focus:outline-none",
                this.chatSize === size
                    ? "bg-white text-blue-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
            ];
        },
        isPageActive() {
            /**
             * Jangan pakai document.hasFocus() untuk menentukan tab aktif.
             * Di mobile/browser tertentu, DevTools, atau saat fokus ada di editor,
             * document.hasFocus() bisa false walaupun halaman detail ticket sedang terlihat.
             */
            return !document.hidden && document.visibilityState === "visible";
        },
        getTicket(silent = false) {
            const self = this;
            if (!silent) {
                self.loading.form = true;
            }
            axios
                .get("api/dashboard/tickets/" + self.$route.params.uuid, {
                    params: { _t: Date.now() }
                })
                .then(function(response) {
                    self.handleTicketResponse(response.data, silent);
                })
                .catch(function() {
                    self.loading.form = false;
                    self.$router.push("/dashboard/tickets");
                });
        },
        handleTicketResponse(ticket, silent = false, eventPayload = null) {
            const previousLatestReplyId = this.getLatestReplyId(
                this.ticket.ticketReplies || []
            );
            const previousIncomingReplyId = this.lastIncomingReplyId;
            const latestReplyId = this.getLatestReplyId(
                ticket.ticketReplies || []
            );
            const latestIncomingReplyId = this.getLatestIncomingReplyId(
                ticket.ticketReplies || []
            );

            const eventReplyId =
                eventPayload && eventPayload.latest_reply_id
                    ? Number(eventPayload.latest_reply_id)
                    : null;

            const eventReplyUserId =
                eventPayload && eventPayload.latest_reply_user_id
                    ? Number(eventPayload.latest_reply_user_id)
                    : null;

            const authUserId = this.$store.state.user
                ? Number(this.$store.state.user.id)
                : null;

            const isIncomingEvent =
                eventReplyUserId !== null &&
                authUserId !== null &&
                eventReplyUserId !== authUserId;

            const incomingReplyAdvanced =
                latestIncomingReplyId !== null &&
                previousIncomingReplyId !== null &&
                latestIncomingReplyId > previousIncomingReplyId;

            const hasNewReply =
                latestReplyId !== null &&
                previousLatestReplyId !== null &&
                latestReplyId > previousLatestReplyId;

            const isIncomingUpdate = isIncomingEvent || incomingReplyAdvanced;
            const dividerReplyId = eventReplyId || latestIncomingReplyId || latestReplyId;
            const pageActive = this.isPageActive();

            this.ticket = ticket;
            this.ticketReply.status_id = ticket.status_id;
            this.lastIncomingReplyId = latestIncomingReplyId;
            this.loading.form = false;

            if (!silent) {
                this.hasNewIncomingReply = false;
                this.newMessageDividerReplyId = null;
                this.markTicketNotificationsAsRead();
                this.scrollToLatestReply(true, "auto");
                return;
            }

            /**
             * Jika halaman detail ticket sedang terlihat dan ada pesan baru dari lawan bicara:
             * - tetap tampilkan divider New message seperti WhatsApp,
             * - tetap auto-scroll ke bawah,
             * - tetap mark-read untuk badge notifikasi global.
             * Divider tidak dihapus otomatis oleh auto-scroll/mark-read,
             * baru hilang saat user klik divider atau saat user mengirim pesan sendiri.
             */
            if (hasNewReply && pageActive && isIncomingUpdate) {
                this.setNewMessageDivider(dividerReplyId);

                setTimeout(() => {
                    this.markTicketNotificationsAsRead();
                }, 500);

                this.scrollToLatestReply(true, "smooth");
                return;
            }

            if (hasNewReply && pageActive) {
                this.hasNewIncomingReply = false;
                this.newMessageDividerReplyId = null;

                this.scrollToLatestReply(true, "smooth");
                return;
            }

            if (hasNewReply && isIncomingUpdate) {
                this.setNewMessageDivider(dividerReplyId);
                return;
            }

            if (pageActive) {
                setTimeout(() => {
                    this.markTicketNotificationsAsRead();
                }, 500);
            }
        },
        queueRealtimeRefresh(payload, clientReceivedTime) {
            const queueStart = performance.now();
            const ticketUuid =
                payload && payload.ticket_uuid
                    ? payload.ticket_uuid
                    : this.$route.params.uuid;

            const latestReplyId =
                payload && payload.latest_reply_id
                    ? Number(payload.latest_reply_id)
                    : null;

            const eventKey = latestReplyId
                ? ticketUuid + ":reply:" + latestReplyId
                : ticketUuid +
                  ":update:" +
                  (payload && payload.updated_at
                      ? payload.updated_at
                      : Date.now());

            if (eventKey === this.lastRealtimeEventKey) {
                console.log("REALTIME_TIMING: Duplicate event ignored, eventKey:", eventKey);
                return;
            }

            this.lastRealtimeEventKey = eventKey;

            if (this.realtimeRefreshTimer) {
                clearTimeout(this.realtimeRefreshTimer);
            }

            console.log("REALTIME_TIMING: Queuing refresh", {
                ticket_id: payload.ticket_id,
                queue_latency_ms: (queueStart - clientReceivedTime).toFixed(2)
            });

            this.realtimeRefreshTimer = setTimeout(() => {
                if (this.realtimeRequestInProgress) {
                    console.log("REALTIME_TIMING: Request already in progress, skipping");
                    return;
                }

                this.realtimeRequestInProgress = true;
                const apiStart = performance.now();

                console.log("REALTIME_TIMING: Starting API fetch", {
                    ticket_id: payload.ticket_id,
                    before_fetch_ms: (apiStart - clientReceivedTime).toFixed(2)
                });

                axios
                    .get("api/dashboard/tickets/" + this.$route.params.uuid, {
                        params: { _t: Date.now() }
                    })
                    .then(response => {
                        const apiEnd = performance.now();
                        console.log("REALTIME_TIMING: API fetch completed", {
                            ticket_id: payload.ticket_id,
                            api_latency_ms: (apiEnd - apiStart).toFixed(2),
                            total_client_latency_ms: (apiEnd - clientReceivedTime).toFixed(2)
                        });
                        this.handleTicketResponse(response.data, true, payload);
                    })
                    .catch(() => {
                        this.lastRealtimeEventKey = null;
                    })
                    .finally(() => {
                        this.realtimeRequestInProgress = false;
                    });
            }, 50);
        },
        markTicketNotificationsAsRead() {
            const ticketUuid =
                this.$route && this.$route.params
                    ? this.$route.params.uuid
                    : null;

            if (
                !ticketUuid ||
                ticketUuid === "undefined" ||
                ticketUuid === "null"
            ) {
                return;
            }

            if (this.markReadInProgress) {
                return;
            }

            this.markReadInProgress = true;

            axios
                .post(
                    "api/dashboard/notifications/tickets/" +
                        ticketUuid +
                        "/mark-read"
                )
                .then(() => {
                    return axios.get("api/dashboard/notifications", {
                        params: {
                            limit: 1,
                            _t: Date.now()
                        }
                    });
                })
                .then(response => {
                    if (this.$store) {
                        this.$store.commit(
                            "setNotificationUnreadTotal",
                            response.data.unread_total || 0
                        );
                    }

                    window.dispatchEvent(
                        new CustomEvent("dashboard-notifications-updated", {
                            detail: response.data
                        })
                    );

                    window.dispatchEvent(
                        new CustomEvent("dashboard-refresh-notifications")
                    );
                })
                .finally(() => {
                    this.markReadInProgress = false;
                });
        },
        startTicketRealtime() {
            this.stopTicketRealtime();

            if (!window.Echo) {
                console.warn("Echo is not available");
                return;
            }

            this.realtimeChannel = window.Echo.private(
                "ticket." + this.$route.params.uuid
            ).listen(".ticket.updated", payload => {
                const clientReceived = performance.now();
                console.log("TICKET REALTIME EVENT:", payload);
                console.log("REALTIME_TIMING: Client received event", {
                    ticket_id: payload.ticket_id,
                    client_received_ms: clientReceived.toFixed(2),
                    server_sent_at: payload.updated_at,
                    payload: payload
                });
                this.queueRealtimeRefresh(payload, clientReceived);
            });
        },
        stopTicketRealtime() {
            if (this.realtimeRefreshTimer) {
                clearTimeout(this.realtimeRefreshTimer);
                this.realtimeRefreshTimer = null;
            }

            this.lastRealtimeEventKey = null;
            this.realtimeRequestInProgress = false;

            if (window.Echo) {
                window.Echo.leave("private-ticket." + this.$route.params.uuid);

                // ini untuk membersihkan listener lama kalau sebelumnya pernah dipakai
                if (this.$store.state.user && this.$store.state.user.id) {
                    window.Echo.leave(
                        "private-users." +
                            this.$store.state.user.id +
                            ".tickets"
                    );
                }
            }

            this.realtimeChannel = null;
            this.realtimeUserChannel = null;
        },
        startVisibleSync() {
            this.stopVisibleSync();

            this.visibleSyncTimer = setInterval(() => {
                if (
                    !this.isPageActive() ||
                    this.loading.reply ||
                    this.loading.file ||
                    this.loading.form
                ) {
                    return;
                }

                this.getTicket(true);
            }, 15000);
        },
        stopVisibleSync() {
            if (this.visibleSyncTimer) {
                clearInterval(this.visibleSyncTimer);
                this.visibleSyncTimer = null;
            }
        },
        updateJumpToBottomButton() {
            this.$nextTick(() => {
                const el = this.$refs.conversationBody;

                if (!el) {
                    this.showJumpToBottomButton = false;
                    return;
                }

                const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                this.showJumpToBottomButton = distanceFromBottom > 180;
            });
        },
        jumpToConversationBottom() {
            this.scrollToLatestReply(true, "smooth");
        },
        acknowledgeNewReplies() {
            this.hasNewIncomingReply = false;
            this.newMessageDividerReplyId = null;
            this.lastIncomingReplyId = this.getLatestIncomingReplyId(
                this.ticket.ticketReplies || []
            );
            this.markTicketNotificationsAsRead();
            this.scrollToLatestReply(true);
            this.showJumpToBottomButton = false;
        },
        setNewMessageDivider(replyId) {
            const numericReplyId = Number(replyId) || null;

            if (!numericReplyId) {
                return;
            }

            if (!this.newMessageDividerReplyId) {
                this.newMessageDividerReplyId = numericReplyId;
            }

            this.hasNewIncomingReply = true;
            this.updateJumpToBottomButton();
        },
        getLatestReplyId(replies) {
            if (!replies || replies.length < 1) {
                return null;
            }

            return Math.max(...replies.map(reply => Number(reply.id) || 0));
        },

        handleConversationScroll() {
            const el = this.$refs.conversationBody;

            if (!el) {
                return;
            }

            const distanceFromBottom =
                el.scrollHeight - el.scrollTop - el.clientHeight;

            this.isNearConversationBottom = distanceFromBottom < 120;
            this.autoScrollEnabled = this.isNearConversationBottom;
            this.showJumpToBottomButton = distanceFromBottom > 180;

        },
        getLatestIncomingReplyId(replies) {
            const incomingReplies = (replies || []).filter(
                reply => !this.isOwnReply(reply)
            );
            if (incomingReplies.length < 1) {
                return null;
            }
            return Math.max(
                ...incomingReplies.map(reply => Number(reply.id) || 0)
            );
        },
        scrollToLatestReply(force = false, behavior = null) {
            this.$nextTick(() => {
                const scrollNow = () => {
                    const el = this.$refs.conversationBody;

                    if (!el) {
                        return;
                    }

                    if (!force && !this.autoScrollEnabled) {
                        return;
                    }

                    const targetTop = el.scrollHeight;
                    const scrollBehavior = behavior || (force ? "auto" : "smooth");

                    if (typeof el.scrollTo === "function") {
                        el.scrollTo({
                            top: targetTop,
                            behavior: scrollBehavior
                        });
                    } else {
                        el.scrollTop = targetTop;
                    }

                    setTimeout(() => {
                        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                        this.isNearConversationBottom = distanceFromBottom < 120;
                        this.autoScrollEnabled = this.isNearConversationBottom;
                        this.showJumpToBottomButton = distanceFromBottom > 180;
                    }, 60);
                };

                /**
                 * Jalankan beberapa kali karena tinggi chat bisa berubah setelah Vue render,
                 * gambar/attachment load, atau editor bawah berubah tinggi.
                 */
                if (window.requestAnimationFrame) {
                    window.requestAnimationFrame(scrollNow);
                } else {
                    scrollNow();
                }

                setTimeout(scrollNow, 80);
                setTimeout(scrollNow, 220);
                setTimeout(scrollNow, 450);
            });
        },
        openReplyComposer(reply = null) {
            if (reply) {
                this.replyingToReply = reply;
            }

            this.composerReady = true;
            this.replyForm = true;

            this.$nextTick(() => {
                this.scheduleBindEditorPasteHandlers();
                setTimeout(() => this.scheduleBindEditorPasteHandlers(), 300);
                setTimeout(() => this.scheduleBindEditorPasteHandlers(), 900);
                this.scrollToLatestReply(true, "auto");
            });
        },
        focusReplyComposer() {
            this.openReplyComposer();
        },
        startReplyToMessage(reply) {
            this.openReplyComposer(reply);
        },
        cancelReplyToMessage() {
            this.replyingToReply = null;
        },
        getReplyAuthorName(reply) {
            if (!reply || !reply.user) {
                return "-";
            }

            return reply.user.name || "-";
        },
        getReplyPreview(reply) {
            if (!reply) {
                return "";
            }

            const text = this.getReplyPlainTextWithoutQuote(reply);
            const maxLength = this.replyQuotePreviewMaxLength || 160;

            if (text.length <= maxLength) {
                return this.escapeHtml(text || "-");
            }

            return this.escapeHtml(text.substring(0, maxLength)) + "...";
        },
        getReplyPlainTextWithoutQuote(reply) {
            if (!reply) {
                return "";
            }

            const html = this.removeQuotedRepliesFromHtml(reply.body || "");
            return this.stripHtml(html);
        },
        removeQuotedRepliesFromHtml(value) {
            const div = document.createElement("div");
            div.innerHTML = value || "";
            Array.prototype.slice
                .call(div.querySelectorAll(".ticket-quoted-reply"))
                .forEach(node => {
                    if (node && node.parentNode) {
                        node.parentNode.removeChild(node);
                    }
                });
            return div.innerHTML;
        },
        stripHtml(value) {
            const div = document.createElement("div");
            div.innerHTML = value || "";
            return (div.textContent || div.innerText || "")
                .replace(/\s+/g, " ")
                .trim();
        },
        escapeHtml(value) {
            return String(value || "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#039;");
        },
        buildReplyPayload() {
            const payload = Object.assign({}, this.ticketReply);
            const hasAttachments = payload.attachments && payload.attachments.length > 0;

            if (hasAttachments && !this.stripHtml(payload.body || "")) {
                payload.body = "<p></p>";
            }

            if (!this.replyingToReply) {
                return payload;
            }

            const author = this.escapeHtml(this.getReplyAuthorName(this.replyingToReply));
            const preview = this.getReplyPreview(this.replyingToReply);
            const originalId = this.replyingToReply.id;
            const currentBody = payload.body || "";

            payload.body =
                '<div class="ticket-quoted-reply" data-reply-id="' +
                originalId +
                '" style="border-left:4px solid #2563eb;background:#eff6ff;padding:8px 10px;margin-bottom:10px;border-radius:8px;cursor:pointer;">' +
                '<div style="font-size:12px;font-weight:600;color:#1e3a8a;">' +
                author +
                '</div>' +
                '<div style="font-size:12px;color:#1d4ed8;">' +
                preview +
                '</div>' +
                '</div>' +
                currentBody;

            return payload;
        },
        handleMessageBodyClick(event) {
            const target = event && event.target ? event.target : null;
            if (!target || typeof target.closest !== "function") {
                return;
            }

            const quoted = target.closest(".ticket-quoted-reply");
            if (quoted) {
                const replyId = quoted.getAttribute("data-reply-id");
                if (replyId) {
                    this.focusReplyById(replyId);
                }
                return;
            }

            const image = target.closest("img");
            if (image && image.getAttribute("src")) {
                this.openImageViewer(
                    image.getAttribute("src"),
                    image.getAttribute("alt") || "image"
                );
            }
        },
        focusReplyById(replyId) {
            const numericReplyId = Number(replyId) || null;
            if (!numericReplyId) {
                return;
            }

            this.replyForm = false;
            this.highlightedReplyId = numericReplyId;

            this.$nextTick(() => {
                const el = this.$el
                    ? this.$el.querySelector('[data-message-reply-id="' + numericReplyId + '"]')
                    : null;

                if (el && typeof el.scrollIntoView === "function") {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                }

                if (this.highlightReplyTimer) {
                    clearTimeout(this.highlightReplyTimer);
                }

                this.highlightReplyTimer = setTimeout(() => {
                    this.highlightedReplyId = null;
                    this.highlightReplyTimer = null;
                }, 2200);
            });
        },
        getCannedReplies() {
            const self = this;
            axios
                .get("api/dashboard/tickets/canned-replies")
                .then(function(response) {
                    self.cannedReplyList = response.data;
                });
        },
        getFilters() {
            const self = this;
            axios.get("api/dashboard/tickets/filters").then(function(response) {
                self.agentList = response.data.agents;
                self.departmentList = response.data.departments;
                self.labelList = response.data.labels;
                self.statusList = response.data.statuses;
            });
        },
        discardReply() {
            this.ticketReply.body = "";
            this.ticketReply.attachments = [];
            this.replyingToReply = null;
            this.replyForm = false;
            this.clearComposerImagePreviews();
        },
        selectUploadFile() {
            if (!this.loading.file) {
                this.$refs.fileInput.click();
            } else {
                this.$notify({
                    title: this.$i18n.t("Error").toString(),
                    text: this.$i18n.t("A file is being uploaded").toString(),
                    type: "warning"
                });
            }
        },
        uploadFile(e) {
            const files = e && e.target && e.target.files
                ? Array.prototype.slice.call(e.target.files)
                : [];

            this.uploadFiles(files);

            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }
        },
        getEditorPasteHandler() {
            if (!this.editorPasteHandler) {
                this.editorPasteHandler = event => {
                    const sourceEvent = event && event.originalEvent ? event.originalEvent : event;
                    this.handleComposerPaste(sourceEvent);
                };
            }

            return this.editorPasteHandler;
        },
        scheduleBindEditorPasteHandlers(attempt = 0) {
            if (this.editorPasteRetryTimer) {
                clearTimeout(this.editorPasteRetryTimer);
                this.editorPasteRetryTimer = null;
            }

            this.editorPasteRetryTimer = setTimeout(() => {
                this.bindEditorPasteHandlers(attempt);
            }, attempt === 0 ? 60 : 180);
        },
        bindEditorPasteHandlers(attempt = 0) {
            const handler = this.getEditorPasteHandler();
            let bound = false;

            if (window.tinymce && window.tinymce.editors) {
                const editors = Array.prototype.slice.call(window.tinymce.editors);

                editors.forEach(editor => {
                    if (!editor) {
                        return;
                    }

                    const editorElement = typeof editor.getElement === "function"
                        ? editor.getElement()
                        : null;

                    const isTicketEditor =
                        editor.id === "ticket_body" ||
                        (editorElement && this.$el && this.$el.contains(editorElement));

                    if (!isTicketEditor) {
                        return;
                    }

                    if (!editor.__ticketImagePasteHandler && typeof editor.on === "function") {
                        editor.on("paste", handler);
                        editor.__ticketImagePasteHandler = handler;
                        bound = true;
                    }

                    const editorDoc = typeof editor.getDoc === "function"
                        ? editor.getDoc()
                        : null;

                    if (editorDoc && !editorDoc.__ticketImagePasteHandler) {
                        editorDoc.addEventListener("paste", handler, true);
                        editorDoc.__ticketImagePasteHandler = handler;
                        bound = true;
                    }
                });
            }

            const bindIframeDocument = iframe => {
                try {
                    const iframeDoc = iframe.contentDocument || (iframe.contentWindow ? iframe.contentWindow.document : null);

                    if (iframeDoc && !iframeDoc.__ticketImagePasteHandler) {
                        iframeDoc.addEventListener("paste", handler, true);
                        iframeDoc.__ticketImagePasteHandler = handler;
                        bound = true;
                    }

                    if (iframe.contentWindow && !iframe.contentWindow.__ticketImagePasteHandler) {
                        iframe.contentWindow.addEventListener("paste", handler, true);
                        iframe.contentWindow.__ticketImagePasteHandler = handler;
                        bound = true;
                    }
                } catch (e) {
                    // Abaikan iframe cross-origin kalau ada.
                }
            };

            const panel = this.$refs.composerPanel;
            if (panel) {
                Array.prototype.slice.call(panel.querySelectorAll("iframe")).forEach(bindIframeDocument);
            }

            // Fallback: beberapa editor menaruh iframe di luar root component.
            Array.prototype.slice.call(document.querySelectorAll("iframe")).forEach(bindIframeDocument);

            if (!bound && attempt < 25 && this.composerReady) {
                this.scheduleBindEditorPasteHandlers(attempt + 1);
            }
        },
        unbindEditorPasteHandlers() {
            const handler = this.editorPasteHandler;

            if (!handler) {
                return;
            }

            if (window.tinymce && window.tinymce.editors) {
                Array.prototype.slice.call(window.tinymce.editors).forEach(editor => {
                    if (editor && editor.__ticketImagePasteHandler && typeof editor.off === "function") {
                        editor.off("paste", editor.__ticketImagePasteHandler);
                        editor.__ticketImagePasteHandler = null;
                    }

                    const editorDoc = editor && typeof editor.getDoc === "function"
                        ? editor.getDoc()
                        : null;

                    if (editorDoc && editorDoc.__ticketImagePasteHandler) {
                        editorDoc.removeEventListener("paste", editorDoc.__ticketImagePasteHandler, true);
                        editorDoc.__ticketImagePasteHandler = null;
                    }
                });
            }

            const unbindIframeDocument = iframe => {
                try {
                    const iframeDoc = iframe.contentDocument || (iframe.contentWindow ? iframe.contentWindow.document : null);
                    if (iframeDoc && iframeDoc.__ticketImagePasteHandler) {
                        iframeDoc.removeEventListener("paste", iframeDoc.__ticketImagePasteHandler, true);
                        iframeDoc.__ticketImagePasteHandler = null;
                    }
                    if (iframe.contentWindow && iframe.contentWindow.__ticketImagePasteHandler) {
                        iframe.contentWindow.removeEventListener("paste", iframe.contentWindow.__ticketImagePasteHandler, true);
                        iframe.contentWindow.__ticketImagePasteHandler = null;
                    }
                } catch (e) {
                    // Abaikan iframe cross-origin kalau ada.
                }
            };

            const panel = this.$refs.composerPanel;
            if (panel) {
                Array.prototype.slice.call(panel.querySelectorAll("iframe")).forEach(unbindIframeDocument);
            }

            Array.prototype.slice.call(document.querySelectorAll("iframe")).forEach(unbindIframeDocument);
        },
        handleDocumentPaste(event) {
            if (!this.replyForm) {
                return;
            }

            // Jangan terlalu ketat cek activeElement, karena TinyMCE/WYSIWYG sering memakai iframe.
            // Pada iframe, paste event bisa berasal dari dokumen editor sehingga document.activeElement
            // di parent tidak selalu berada di dalam composer.
            this.handleComposerPaste(event);
        },
        handleComposerPaste(event) {
            const sourceEvent = event && event.originalEvent ? event.originalEvent : event;
            const clipboardData = sourceEvent && sourceEvent.clipboardData
                ? sourceEvent.clipboardData
                : null;

            if (!clipboardData) {
                return;
            }

            const files = this.extractImageFilesFromClipboard(clipboardData);

            if (files.length < 1) {
                return;
            }

            const signature = files
                .map(file => [file.name || "clipboard-image", file.size, file.type, file.lastModified || 0].join(":"))
                .join("|");

            if (signature && signature === this.lastPasteSignature) {
                if (sourceEvent && typeof sourceEvent.preventDefault === "function") {
                    sourceEvent.preventDefault();
                }
                return;
            }

            this.lastPasteSignature = signature;
            setTimeout(() => {
                this.lastPasteSignature = null;
            }, 800);

            if (sourceEvent && typeof sourceEvent.preventDefault === "function") {
                sourceEvent.preventDefault();
            }

            if (sourceEvent && typeof sourceEvent.stopPropagation === "function") {
                sourceEvent.stopPropagation();
            }

            this.openReplyComposer();
            this.uploadFiles(files, "paste");
        },
        extractImageFilesFromClipboard(clipboardData) {
            const files = [];

            if (clipboardData.items) {
                Array.prototype.slice.call(clipboardData.items).forEach(item => {
                    if (item.kind === "file") {
                        const file = item.getAsFile();
                        if (file && this.isImageFile(file)) {
                            files.push(file);
                        }
                    }
                });
            }

            if (files.length < 1 && clipboardData.files) {
                Array.prototype.slice.call(clipboardData.files).forEach(file => {
                    if (this.isImageFile(file)) {
                        files.push(file);
                    }
                });
            }

            return files;
        },
        handleComposerDrop(event) {
            const files = event && event.dataTransfer && event.dataTransfer.files
                ? Array.prototype.slice.call(event.dataTransfer.files)
                : [];

            if (files.length < 1) {
                return;
            }

            this.openReplyComposer();
            this.uploadFiles(files, "drop");
        },
        uploadFiles(files, source = "file") {
            const list = Array.prototype.slice.call(files || []);

            if (list.length < 1) {
                return;
            }

            this.openReplyComposer();

            list.forEach(file => {
                if (this.maxFileSize && file.size > this.maxFileSize) {
                    this.$notify({
                        title: this.$i18n.t("Error").toString(),
                        text: this.$i18n
                            .t("The file exceeds the maximum size of {size}", {
                                size: this.maxFileSizeLabel
                            })
                            .toString(),
                        type: "warning"
                    });
                    return;
                }

                if (this.isImageFile(file)) {
                    this.uploadSingleImage(file, source);
                } else {
                    this.uploadSingleAttachment(file);
                }
            });
        },
        uploadSingleAttachment(file) {
            const self = this;
            const formData = new FormData();

            formData.append("file", file);
            self.pendingUploadCount += 1;
            self.loading.file = true;

            axios
                .post("api/dashboard/tickets/attachments", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: function(progressEvent) {
                        if (!progressEvent.total) {
                            return;
                        }

                        self.uploadingFileProgress = Math.round(
                            (progressEvent.loaded / progressEvent.total) * 100
                        );
                    }
                })
                .then(function(response) {
                    self.ticketReply.attachments.push(response.data);
                })
                .catch(function() {
                    self.$notify({
                        title: self.$i18n.t("Error").toString(),
                        text: self.$i18n.t("File upload failed").toString(),
                        type: "error"
                    });
                })
                .finally(function() {
                    self.pendingUploadCount = Math.max(self.pendingUploadCount - 1, 0);
                    self.loading.file = self.pendingUploadCount > 0;
                    if (!self.loading.file) {
                        self.uploadingFileProgress = 0;
                    }
                });
        },
        uploadSingleImage(file, source = "file") {
            const self = this;
            const preview = this.createComposerImagePreview(file, source);
            const formData = new FormData();

            formData.append("file", file);
            self.pendingUploadCount += 1;
            self.loading.file = true;

            axios
                .post("api/dashboard/tickets/attachments", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: function(progressEvent) {
                        if (!progressEvent.total) {
                            return;
                        }

                        const percent = Math.round(
                            (progressEvent.loaded / progressEvent.total) * 100
                        );

                        preview.progress = percent;
                        self.uploadingFileProgress = percent;
                    }
                })
                .then(function(response) {
                    preview.status = "uploaded";
                    preview.progress = 100;
                    preview.attachment = response.data;
                    preview.attachmentId = self.getAttachmentId(response.data);
                    self.ticketReply.attachments.push(response.data);
                })
                .catch(function() {
                    preview.status = "error";
                    preview.progress = 0;
                    self.$notify({
                        title: self.$i18n.t("Error").toString(),
                        text: self.$i18n.t("Image upload failed").toString(),
                        type: "error"
                    });
                })
                .finally(function() {
                    self.pendingUploadCount = Math.max(self.pendingUploadCount - 1, 0);
                    self.loading.file = self.pendingUploadCount > 0;
                    if (!self.loading.file) {
                        self.uploadingFileProgress = 0;
                    }
                });
        },
        createComposerImagePreview(file, source = "file") {
            const preview = {
                key: Date.now() + "-" + Math.random().toString(16).slice(2),
                name: file.name || (source === "paste" ? "pasted-image.png" : "image.png"),
                size: file.size || 0,
                type: file.type || "image/png",
                status: "uploading",
                progress: 0,
                url: URL.createObjectURL(file),
                attachment: null,
                attachmentId: null
            };

            this.composerImagePreviews.push(preview);
            this.$nextTick(() => this.scrollToLatestReply(true, "smooth"));

            return preview;
        },
        removeComposerImagePreview(preview) {
            if (!preview) {
                return;
            }

            if (preview.url) {
                URL.revokeObjectURL(preview.url);
            }

            this.composerImagePreviews = this.composerImagePreviews.filter(item => item.key !== preview.key);

            if (preview.attachmentId) {
                this.ticketReply.attachments = this.ticketReply.attachments.filter(attachment => {
                    return this.getAttachmentId(attachment) !== preview.attachmentId;
                });
            }
        },
        clearComposerImagePreviews() {
            this.revokeComposerImagePreviews();
            this.composerImagePreviews = [];
        },
        revokeComposerImagePreviews() {
            (this.composerImagePreviews || []).forEach(preview => {
                if (preview && preview.url) {
                    URL.revokeObjectURL(preview.url);
                }
            });
        },
        isImageFile(file) {
            return !!(file && file.type && file.type.indexOf("image/") === 0);
        },
        visibleReplyAttachments(reply) {
            const attachments = reply && reply.attachments ? reply.attachments : [];
            const hasInlineImage = this.replyBodyContainsInlineImage(reply);

            return attachments.filter(attachment => {
                if (hasInlineImage && this.isImageAttachment(attachment)) {
                    return false;
                }

                return true;
            });
        },
        replyBodyContainsInlineImage(reply) {
            return !!(reply && reply.body && /<img\s/i.test(reply.body));
        },
        isImageAttachment(attachment) {
            if (!attachment) {
                return false;
            }

            const mimeType = attachment.mime_type || attachment.mime || attachment.type || attachment.file_type || "";
            const name = this.getAttachmentName(attachment).toLowerCase();

            return (
                String(mimeType).indexOf("image/") === 0 ||
                /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)
            );
        },
        openImageViewer(url, name = null) {
            if (!url) {
                return;
            }

            this.imageViewerUrl = url;
            this.imageViewerName = name || "";
            this.imageViewerOpen = true;
        },
        closeImageViewer() {
            this.imageViewerOpen = false;
            this.imageViewerUrl = null;
            this.imageViewerName = null;
        },
        getAttachmentPreviewUrl(attachment) {
            if (!attachment) {
                return null;
            }

            return (
                attachment.url ||
                attachment.full_url ||
                attachment.preview_url ||
                attachment.download_url ||
                attachment.download ||
                attachment.path_url ||
                attachment.file_url ||
                attachment.temporary_url ||
                attachment.src ||
                null
            );
        },
        getAttachmentName(attachment) {
            if (!attachment) {
                return "image";
            }

            return (
                attachment.name ||
                attachment.file_name ||
                attachment.filename ||
                attachment.original_name ||
                attachment.original_filename ||
                "image"
            );
        },
        getAttachmentId(attachment) {
            if (!attachment) {
                return null;
            }

            return attachment.id || attachment.uuid || attachment.attachment_id || attachment.name || null;
        },
        removeAttachment(attachment) {
            const removed = this.ticketReply.attachments[attachment];
            const removedId = this.getAttachmentId(removed);

            this.ticketReply.attachments.splice(attachment, 1);

            if (removedId) {
                const preview = this.composerImagePreviews.find(item => item.attachmentId === removedId);
                if (preview) {
                    this.removeComposerImagePreview(preview);
                }
            }
        },
        startEditReply(ticketReply) {
            this.editingReplyId = ticketReply.id;
            const parts = this.splitReplyQuoteAndBody(ticketReply.body || "");
            this.editingReplyQuoteHtml = parts.quoteHtml;
            this.editingReplyBody = this.stripHtml(parts.bodyHtml).trim();
        },
        cancelEditReply() {
            this.editingReplyId = null;
            this.editingReplyBody = "";
            this.editingReplyQuoteHtml = "";
        },
        normalizeEditReplyBody(value) {
            const text = String(value || "").trim();

            if (!text) {
                return "<p></p>";
            }

            return "<p>" +
                this.escapeHtml(text).replace(/\n/g, "<br>") + "</p>";
        },
        splitReplyQuoteAndBody(value) {
            const div = document.createElement("div");
            div.innerHTML = value || "";
            let quoteHtml = "";

            Array.prototype.slice
                .call(div.querySelectorAll(".ticket-quoted-reply"))
                .forEach(node => {
                    if (!quoteHtml) {
                        quoteHtml = node.outerHTML;
                    }

                    if (node && node.parentNode) {
                        node.parentNode.removeChild(node);
                    }
                });

            return {
                quoteHtml: quoteHtml,
                bodyHtml: div.innerHTML
            };
        },
        updateReply(ticketReply) {
            const self = this;
            const body = (self.editingReplyQuoteHtml || "") + self.normalizeEditReplyBody(self.editingReplyBody);

            self.loading.editReply = true;
            axios
                .put(
                    "api/dashboard/tickets/" +
                        self.$route.params.uuid +
                        "/reply/" +
                        ticketReply.id,
                    {
                        body: body
                    }
                )
                .then(function(response) {
                    self.ticket = response.data.ticket;
                    self.ticketReply.status_id = response.data.ticket.status_id;
                    self.cancelEditReply();
                    self.loading.editReply = false;
                })
                .catch(function() {
                    self.loading.editReply = false;
                });
        },
        removeLabel(index) {
            const self = this;
            const label = self.ticket.labels[index];
            axios
                .post(
                    "api/dashboard/tickets/" +
                        self.$route.params.uuid +
                        "/remove-label",
                    {
                        label: label.id
                    }
                )
                .then(function() {
                    self.ticket.labels.splice(index, 1);
                });
        },
        addReply() {
            const self = this;

            if (self.loading.reply) {
                return;
            }

            self.loading.reply = true;

            axios
                .post(
                    "api/dashboard/tickets/" +
                        self.$route.params.uuid +
                        "/reply",
                    self.buildReplyPayload()
                )
                .then(function(response) {
                    self.ticket = response.data.ticket;
                    self.ticketReply.status_id = response.data.ticket.status_id;
                    self.lastIncomingReplyId = self.getLatestIncomingReplyId(
                        response.data.ticket.ticketReplies || []
                    );
                    self.hasNewIncomingReply = false;
                    self.newMessageDividerReplyId = null;
                    self.lastRealtimeReplyId = null;
                    self.discardReply();
                    self.scrollToLatestReply(true);
                })
                .catch(function(error) {
                    self.$notify({
                        title: self.$i18n.t("Error").toString(),
                        text:
                            error.response &&
                            error.response.data &&
                            error.response.data.message
                                ? error.response.data.message
                                : self.$i18n
                                      .t("An error occurred while saving data")
                                      .toString(),
                        type: "error"
                    });
                })
                .finally(function() {
                    self.loading.reply = false;
                });
        },
        approveTicket() {
            const self = this;
            axios
                .post(
                    "api/dashboard/tickets/" +
                        self.$route.params.uuid +
                        "/quick-actions",
                    {
                        action: "approve"
                    }
                )
                .then(function(response) {
                    self.ticket = response.data.ticket;
                    self.ticketReply.status_id = response.data.ticket.status_id;
                    self.$notify({
                        title: self.$i18n.t("Success").toString(),
                        text: self.$i18n
                            .t("Ticket approved successfully")
                            .toString(),
                        type: "success"
                    });
                });
        },
        rejectTicket() {
            const self = this;
            axios
                .post(
                    "api/dashboard/tickets/" +
                        self.$route.params.uuid +
                        "/quick-actions",
                    {
                        action: "reject"
                    }
                )
                .then(function(response) {
                    self.ticket = response.data.ticket;
                    self.$notify({
                        title: self.$i18n.t("Success").toString(),
                        text: self.$i18n
                            .t("Ticket rejected successfully")
                            .toString(),
                        type: "success"
                    });
                });
        },
        closeMobileAside() {
            this.mobileAsideOpen = false;
        },
        /**
         * Kirim ulang notifikasi chat terbaru tiket ke semua peserta.
         * Server membatasi 1x per 10 menit; bila ditolak, tombol
         * dinonaktifkan selama sisa cooldown.
         */
        followUpTicket() {
            const self = this;
            if (self.followUp.loading || self.followUp.remainingMinutes > 0) {
                return;
            }
            self.followUp.loading = true;
            axios
                .post(
                    "api/dashboard/tickets/" +
                        self.$route.params.uuid +
                        "/follow-up"
                )
                .then(function() {
                    self.$notify({
                        title: self.$i18n.t("Success").toString(),
                        text: self.$i18n
                            .t("Follow-up notification sent")
                            .toString(),
                        type: "success"
                    });
                })
                .catch(function(error) {
                    const response = error.response;
                    if (response && response.status === 429) {
                        const minutes = response.data.retry_after_minutes;
                        self.startFollowUpCooldown(minutes);
                        self.$notify({
                            title: self.$i18n.t("Error").toString(),
                            text: self.$i18n
                                .t(
                                    "Please wait {minutes} more minutes before following up again",
                                    { minutes: minutes }
                                )
                                .toString(),
                            type: "error"
                        });
                        return;
                    }
                    self.$notify({
                        title: self.$i18n.t("Error").toString(),
                        text: (response && response.data && response.data.message) ||
                            self.$i18n
                                .t("An error occurred while saving data")
                                .toString(),
                        type: "error"
                    });
                })
                .finally(function() {
                    self.followUp.loading = false;
                });
        },
        startFollowUpCooldown(minutes) {
            const self = this;
            if (self.followUpTimer) {
                clearInterval(self.followUpTimer);
            }
            const remaining = parseInt(minutes, 10);
            if (!remaining || remaining < 1) {
                self.followUp.remainingMinutes = 0;
                return;
            }
            self.followUp.remainingMinutes = remaining;
            self.followUpTimer = setInterval(function() {
                self.followUp.remainingMinutes -= 1;
                if (self.followUp.remainingMinutes < 1) {
                    self.followUp.remainingMinutes = 0;
                    clearInterval(self.followUpTimer);
                    self.followUpTimer = null;
                }
            }, 60000);
        },
        onAsideAction(action) {
            if (action === "approve") {
                this.approveTicket();
            } else if (action === "reject") {
                this.rejectTicket();
            }
        },
        closeActionDropdown() {
            this.actions.agent = false;
            this.actions.department = false;
            this.actions.label = false;
        },
        toggleActionDropdown(action) {
            if (action === "agent") {
                this.actions.agent = !this.actions.agent;
                this.actions.department = false;
                this.actions.label = false;
            }
            if (action === "department") {
                this.actions.department = !this.actions.department;
                this.actions.agent = false;
                this.actions.label = false;
            }
            if (action === "label") {
                this.actions.label = !this.actions.label;
                this.actions.agent = false;
                this.actions.department = false;
            }
        },
        action(param, value) {
            const self = this;
            axios
                .post(
                    "api/dashboard/tickets/" +
                        self.$route.params.uuid +
                        "/quick-actions",
                    {
                        action: param,
                        value: value
                    }
                )
                .then(function(response) {
                    self.closeActionDropdown();
                    if (!response.data.access) {
                        self.$router.push("/dashboard/tickets");
                    } else {
                        self.ticket = response.data.ticket;
                        self.ticketReply.status_id =
                            response.data.ticket.status_id;
                    }
                })
                .catch(function() {
                    self.closeActionDropdown();
                });
        },
        deleteTicket() {
            const self = this;
            axios
                .delete("api/dashboard/tickets/" + self.$route.params.uuid)
                .then(function() {
                    self.$notify({
                        title: self.$i18n.t("Success").toString(),
                        text: self.$i18n
                            .t("Data deleted successfully")
                            .toString(),
                        type: "success"
                    });
                    self.$router.push("/dashboard/tickets");
                })
                .catch(function() {
                    self.deleteTicketModal = false;
                });
        },
        roleBadgeLabel(role) {
            if (role === "creator") {
                return this.$t("Creator");
            }
            if (role === "target") {
                return this.$t("Executor");
            }
            if (role === "approver") {
                return this.$t("Approver");
            }
            return role;
        },
        roleBadgeClass(role) {
            if (role === "creator") {
                return "bg-blue-100 text-blue-800";
            }
            if (role === "target") {
                return "bg-green-100 text-green-800";
            }
            if (role === "approver") {
                return "bg-purple-100 text-purple-800";
            }
            return "bg-gray-100 text-gray-800";
        },
        isOwnReply(reply) {
            return !!(
                this.$store.state.user &&
                reply &&
                reply.user &&
                Number(reply.user.id) === Number(this.$store.state.user.id)
            );
        },
        getReplyAvatar(reply) {
            const user = reply && reply.user ? reply.user : null;

            if (!user) {
                return null;
            }

            if (user.avatar && user.avatar !== "gravatar") {
                return user.avatar;
            }

            if (user.gravatar) {
                return user.gravatar;
            }

            return null;
        },
        getReplyInitials(reply) {
            const name = this.getReplyAuthorName(reply);

            if (!name || name === "-") {
                return "?";
            }

            return name
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map(part => part.charAt(0).toUpperCase())
                .join("");
        },
        toggleSeenBy(replyId) {
            this.$set(this.seenByOpen, replyId, !this.seenByOpen[replyId]);
        },
        formatReadAt(value) {
            return moment(value)
                .locale(window.app.app_date_locale)
                .format(window.app.app_date_format + " HH:mm");
        }
    }
};
</script>

<style scoped>
.ticket-theme {
    --ticket-accent: #2563eb;
    --ticket-accent-hover: #1d4ed8;
}

.ticket-aside-toggle-default {
    background: var(--ticket-accent);
    border: 1px solid var(--ticket-accent);
    color: #ffffff;
}

.ticket-aside-toggle-default:hover {
    background: var(--ticket-accent-hover);
    border-color: var(--ticket-accent-hover);
}

.ticket-aside-toggle-urgent {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border: 1px solid #dc2626;
    color: #ffffff;
}

.ticket-aside-toggle-urgent:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.ticket-aside-toggle-badge {
    background: #ffffff;
    color: #dc2626;
}

.ticket-aside-drawer-backdrop {
    background: rgba(15, 23, 42, 0.55);
}

.ticket-aside-drawer-panel {
    -webkit-overflow-scrolling: touch;
}

.ticket-quoted-reply {
    border-left: 4px solid var(--ticket-accent);
    background: #eff6ff;
    padding: 8px 10px;
    margin-bottom: 10px;
    border-radius: 8px;
}

.ticket-messages {
    background:
        radial-gradient(circle at top left, rgba(148, 163, 184, 0.12), transparent 24rem),
        #f8fafc;
}

.ticket-reply-item {
    border-top: 0 !important;
    gap: 0.65rem;
}

.ticket-message-bubble {
    word-break: break-word;
}

.ticket-message-avatar {
    width: 2.1rem;
    height: 2.1rem;
    border-radius: 9999px;
    background: #dbeafe;
    color: #1e40af;
    border: 1px solid #bfdbfe;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

.ticket-message-avatar-right {
    background: #dbeafe;
}

.ticket-message-avatar-left {
    background: #f1f5f9;
    color: #334155;
    border-color: #cbd5e1;
}

.ticket-composer {
    padding: 0.75rem 1rem;
    box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.06);
}

.ticket-chat-size-compact .ticket-reply-item {
    padding: 0.45rem 1rem;
}

.ticket-chat-size-compact .ticket-message-bubble {
    max-width: 38rem;
    padding: 0.65rem 0.8rem;
    border-radius: 1rem;
}

.ticket-chat-size-compact .ticket-message-author {
    font-size: 0.875rem;
}

.ticket-chat-size-compact .ticket-message-meta,
.ticket-chat-size-compact .ticket-message-body {
    font-size: 0.8125rem;
}

.ticket-chat-size-compact .ticket-composer {
    padding: 0.55rem 0.75rem;
}

.ticket-chat-size-compact .ticket-message-avatar {
    width: 1.8rem;
    height: 1.8rem;
    font-size: 0.68rem;
}

.ticket-chat-size-normal .ticket-reply-item {
    padding: 0.75rem 1.5rem;
}

.ticket-chat-size-normal .ticket-message-bubble {
    max-width: 46rem;
    padding: 0.95rem 1rem;
    border-radius: 1.1rem;
}

.ticket-chat-size-normal .ticket-message-author {
    font-size: 1rem;
}

.ticket-chat-size-normal .ticket-message-meta,
.ticket-chat-size-normal .ticket-message-body {
    font-size: 0.9rem;
}

.ticket-chat-size-comfortable .ticket-reply-item {
    padding: 1rem 2rem;
}

.ticket-chat-size-comfortable .ticket-message-bubble {
    max-width: 54rem;
    padding: 1.2rem 1.3rem;
    border-radius: 1.25rem;
}

.ticket-chat-size-comfortable .ticket-message-author {
    font-size: 1.075rem;
}

.ticket-chat-size-comfortable .ticket-message-meta,
.ticket-chat-size-comfortable .ticket-message-body {
    font-size: 1rem;
}

.ticket-chat-size-comfortable .ticket-composer {
    padding: 1rem 1.25rem;
}

.ticket-chat-size-comfortable .ticket-message-avatar {
    width: 2.4rem;
    height: 2.4rem;
    font-size: 0.82rem;
}

.ticket-message-body >>> p {
    margin-bottom: 0.5rem;
}

.ticket-message-body >>> p:last-child {
    margin-bottom: 0;
}


.ticket-message-bubble-own {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    border-color: #60a5fa !important;
    color: #0f172a;
}

.ticket-message-bubble-other {
    background: #ffffff;
    border-color: #94a3b8 !important;
    color: #0f172a;
}

.ticket-reply-highlight .ticket-message-bubble {
    animation: ticketReplyHighlight 2.2s ease-in-out;
    box-shadow: 0 0 0 3px rgba(250, 204, 21, 0.85), 0 12px 28px rgba(15, 23, 42, 0.18);
}

@keyframes ticketReplyHighlight {
    0% {
        transform: scale(1);
        box-shadow: 0 0 0 5px rgba(250, 204, 21, 0.95), 0 12px 28px rgba(15, 23, 42, 0.2);
    }
    45% {
        transform: scale(1.015);
        box-shadow: 0 0 0 5px rgba(250, 204, 21, 0.75), 0 12px 28px rgba(15, 23, 42, 0.2);
    }
    100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(250, 204, 21, 0), 0 1px 3px rgba(15, 23, 42, 0.08);
    }
}

.ticket-message-body >>> .ticket-quoted-reply {
    cursor: pointer;
    background: #dbeafe !important;
    border-left-color: var(--ticket-accent) !important;
    transition: background-color 0.15s ease, transform 0.15s ease;
}

.ticket-message-body >>> .ticket-quoted-reply:hover {
    background: #bfdbfe !important;
    transform: translateX(2px);
}

.ticket-composer-panel {
    max-height: 48vh;
    overflow-y: auto;
    box-shadow: 0 12px 32px -8px rgba(15, 23, 42, 0.14), 0 4px 12px -4px rgba(15, 23, 42, 0.08);
    animation: ticketComposerIn 0.18s ease-out;
}

@keyframes ticketComposerIn {
    from {
        opacity: 0;
        transform: translateY(6px) scale(0.995);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.ticket-composer-header {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    backdrop-filter: blur(8px);
}

.ticket-composer-panel:focus-within {
    border-color: #bfdbfe;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08), 0 12px 32px -8px rgba(15, 23, 42, 0.14);
}

.ticket-attach-button {
    height: 2.5rem;
    min-width: 2.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #d1d5db;
    color: #2563eb;
    background: #ffffff;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.ticket-attach-button:hover {
    background: #eff6ff;
    color: #1d4ed8;
}

.ticket-attach-button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.ticket-image-preview-strip {
    display: flex;
    gap: 0.65rem;
    overflow-x: auto;
    padding-bottom: 0.25rem;
}

.ticket-image-preview-card {
    width: 5.25rem;
    height: 5.25rem;
    border-radius: 0.85rem;
    border: 1px solid #bfdbfe;
    overflow: hidden;
    position: relative;
    flex: 0 0 auto;
    background: #eff6ff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.ticket-image-preview-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ticket-image-preview-remove {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 9999px;
    background: rgba(15, 23, 42, 0.72);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    line-height: 1;
}

.ticket-image-preview-status {
    position: absolute;
    inset: auto 0 0 0;
    padding: 0.25rem;
    background: rgba(37, 99, 235, 0.88);
    color: #ffffff;
    font-size: 0.65rem;
    font-weight: 700;
    text-align: center;
}

.ticket-image-preview-error {
    background: rgba(220, 38, 38, 0.9);
}

.ticket-action-button,
.ticket-submit-button {
    background: var(--ticket-accent);
    border: 1px solid var(--ticket-accent);
    transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.ticket-action-button:hover,
.ticket-submit-button:hover {
    background: var(--ticket-accent-hover);
    border-color: var(--ticket-accent-hover);
}

.ticket-action-button:active,
.ticket-submit-button:active {
    transform: translateY(1px);
}

.ticket-action-button:focus,
.ticket-submit-button:focus {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.22);
}


.ticket-chat-attachments {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
    gap: 0.5rem;
}

.ticket-chat-image-link {
    display: block;
    position: relative;
    overflow: hidden;
    border-radius: 0.9rem;
    border: 1px solid #bfdbfe;
    background: #eff6ff;
    min-height: 8rem;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.ticket-chat-image {
    display: block;
    width: 100%;
    height: 11rem;
    object-fit: cover;
}

.ticket-chat-image-name {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 0.35rem 0.55rem;
    font-size: 0.7rem;
    color: #ffffff;
    background: linear-gradient(to top, rgba(15, 23, 42, 0.72), rgba(15, 23, 42, 0));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ticket-jump-bottom {
    position: absolute;
    right: 1.25rem;
    bottom: 5.3rem;
    z-index: 15;
    width: 2.65rem;
    height: 2.65rem;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    color: var(--ticket-accent);
    border: 1px solid #bfdbfe;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.18);
    transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.ticket-jump-bottom:hover {
    background: #eff6ff;
    transform: translateY(-1px);
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.22);
}

.ticket-jump-bottom-badge {
    position: absolute;
    top: -0.55rem;
    right: -0.35rem;
    min-width: 2rem;
    padding: 0.1rem 0.35rem;
    border-radius: 9999px;
    background: #ef4444;
    color: #ffffff;
    font-size: 0.65rem;
    font-weight: 700;
    line-height: 1rem;
}

/* Mobile clean layout */
.ticket-toolbar {
    min-height: 3.25rem;
}

.ticket-toolbar .btn {
    padding: 0.75rem !important;
}

.ticket-toolbar-actions {
    overflow-x: auto;
    scrollbar-width: none;
}

.ticket-toolbar-actions::-webkit-scrollbar {
    display: none;
}

.ticket-toolbar-meta {
    flex-shrink: 0;
}

.ticket-id-label {
    line-height: 1;
}

.ticket-detail-header {
    box-shadow: 0 1px 0 rgba(15, 23, 42, 0.05);
}

.ticket-detail-header-inner {
    min-height: 3.3rem;
}

.ticket-size-switch button {
    white-space: nowrap;
}

.ticket-message-bubble {
    width: auto !important;
    max-width: min(76%, 46rem) !important;
    min-width: 0;
}

.ticket-message-meta {
    gap: 0.35rem;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.ticket-message-meta button {
    white-space: nowrap;
}

.ticket-message-body {
    overflow-wrap: anywhere;
}

.ticket-message-body >>> * {
    max-width: 100%;
}

.ticket-message-body >>> img {
    height: auto;
    border-radius: 0.75rem;
}

@media (max-width: 640px) {
    .ticket-toolbar {
        min-height: 2.75rem;
    }

    .ticket-toolbar .btn {
        padding: 0.55rem !important;
    }

    .ticket-toolbar svg {
        width: 1rem !important;
        height: 1rem !important;
    }

    .ticket-toolbar-meta {
        margin: 0.25rem !important;
    }

    .ticket-id-label {
        font-size: 1.15rem;
    }

    .ticket-toolbar-meta .btn-white,
    .ticket-toolbar-meta a.btn {
        display: none !important;
    }

    .ticket-detail-header-inner {
        min-height: 2.85rem;
        padding: 0;
    }

    .ticket-detail-main {
        padding-top: 0.45rem !important;
        padding-bottom: 0.45rem !important;
    }

    .ticket-detail-main .text-base,
    .ticket-detail-main .text-lg {
        font-size: 0.95rem !important;
        line-height: 1.25rem !important;
    }

    .ticket-detail-controls {
        padding: 0.4rem 0.55rem !important;
    }

    .ticket-size-switch {
        transform: scale(0.82);
        transform-origin: right center;
    }

    .ticket-size-switch button {
        padding: 0.25rem 0.45rem !important;
        font-size: 0.68rem !important;
    }

    .ticket-messages {
        background: #f8fafc;
    }

    .ticket-reply-item {
        padding: 0.45rem 0.55rem !important;
        gap: 0.4rem !important;
        align-items: flex-end;
    }

    .ticket-message-avatar {
        width: 1.8rem !important;
        height: 1.8rem !important;
        font-size: 0.66rem !important;
        margin-bottom: 0.15rem;
    }

    .ticket-message-bubble {
        max-width: calc(100% - 2.4rem) !important;
        padding: 0.65rem 0.75rem !important;
        border-radius: 1rem !important;
    }

    .ticket-message-author {
        font-size: 0.9rem !important;
        line-height: 1.15rem !important;
    }

    .ticket-message-meta {
        float: none !important;
        justify-content: flex-start;
        font-size: 0.75rem !important;
        line-height: 1rem !important;
        margin-top: 0.2rem;
    }

    .ticket-message-meta .rounded-full {
        padding: 0.15rem 0.45rem !important;
        font-size: 0.7rem !important;
    }

    .ticket-message-body {
        font-size: 0.86rem !important;
        line-height: 1.35rem !important;
        margin-top: 0.35rem;
    }

    .ticket-composer {
        padding: 0.45rem 0.55rem !important;
    }

    .ticket-action-button {
        padding-top: 0.6rem !important;
        padding-bottom: 0.6rem !important;
        font-size: 0.85rem !important;
    }

    .ticket-composer-panel {
        border-radius: 1rem !important;
        max-height: 42vh;
    }

    .ticket-composer-panel form {
        padding: 0.55rem !important;
    }

    .ticket-composer-panel .form-select {
        max-width: 8.5rem;
        font-size: 0.75rem !important;
    }

    .ticket-submit-button {
        padding-left: 0.65rem !important;
        padding-right: 0.65rem !important;
        font-size: 0.75rem !important;
    }

    .ticket-jump-bottom {
        right: 0.85rem;
        bottom: 4.4rem;
        width: 2.45rem;
        height: 2.45rem;
    }

    .ticket-chat-attachments {
        grid-template-columns: 1fr;
    }

    .ticket-chat-image {
        height: 10rem;
    }

}


.ticket-message-body >>> .ticket-inline-image,
.ticket-message-body >>> img.ticket-inline-image {
    max-width: min(100%, 360px);
    height: auto;
    border-radius: 0.85rem;
    display: block;
    margin: 0.35rem 0;
    border: 1px solid rgba(148, 163, 184, 0.35);
}

@media (max-width: 640px) {
    .ticket-message-body >>> .ticket-inline-image,
    .ticket-message-body >>> img.ticket-inline-image {
        max-width: 100%;
    }
}


.ticket-chat-image-link {
    text-align: left;
    appearance: none;
    border: 0;
    padding: 0;
    background: transparent;
    cursor: zoom-in;
}

</style>
